import { useQuery } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { labelForAction } from '@fint/validation';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { formatDateTime } from '@/lib/dates';
import { Card, CardContent } from '@/features/common/ui';
import { describeInvoiceChanges, type InvoiceChangeRecord } from '@/features/audit/changes';

/**
 * What has happened to this invoice, newest first: issued, edited (with
 * the reason and what changed), shared, voided, written off.
 *
 * ── WHY ON THE INVOICE ──
 * The Activity page has every event in the book; the question asked on an
 * invoice is "why does this say ₹10,500.00 when the PDF I sent said
 * ₹10,500.01". The answer is the edit's own record - who, when, the
 * reason they typed, and the figures before and after - and it belongs
 * beside the document, not three filters away. Read through the same
 * audit endpoint, scoped to this invoice, so the two never disagree.
 *
 * Needs `audit.view`; a role without it sees no panel rather than an error.
 */
interface AuditEvent {
  id: string;
  action: string;
  metadata: Record<string, unknown> | null;
  createdAt: string;
  actorName: string | null;
  actorEmail: string | null;
}

export function InvoiceHistory({ invoiceId }: { invoiceId: string }) {
  const org = useActiveOrg();
  const perms = usePermissions();
  const events = useQuery({
    queryKey: ['audit', org?.id, 'invoice', invoiceId],
    queryFn: () => api.get<{ items: AuditEvent[] }>(`/audit?entityType=invoice&entityId=${invoiceId}&limit=50`),
    enabled: !!org?.id && perms.can('audit', 'view'),
  });
  if (!perms.can('audit', 'view') || events.isError) return null;
  const items = events.data?.items ?? [];
  if (items.length === 0) return null;

  return (
    <Card className="print:hidden" data-testid="invoice-history">
      <CardContent className="pt-5">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <History className="size-4 text-muted-foreground" aria-hidden />
          History
        </h2>
        <ol className="space-y-3">
          {items.map((event) => {
            const reason = typeof event.metadata?.reason === 'string' ? event.metadata.reason : null;
            const changes = describeInvoiceChanges(event.metadata?.changes as InvoiceChangeRecord | undefined);
            const unchanged = event.metadata?.unchanged === true;
            const via = event.metadata?.via as { kind?: string } | undefined;
            return (
              <li key={event.id} className="text-sm" data-testid="invoice-history-row">
                <p>
                  <span className="font-medium">{labelForAction(event.action)}</span>
                  <span className="text-muted-foreground">
                    {' · '}
                    {formatDateTime(event.createdAt)}
                    {event.actorName || event.actorEmail ? ` · ${event.actorName || event.actorEmail}` : ''}
                    {via ? ' · via AI agent' : ''}
                  </span>
                </p>
                {reason ? (
                  <p className="text-sm" data-testid="invoice-history-reason">
                    “{reason}”
                  </p>
                ) : null}
                {changes.length ? (
                  <ul className="mt-0.5 text-xs text-muted-foreground">
                    {changes.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                ) : unchanged ? (
                  <p className="text-xs text-muted-foreground">Saved with nothing changed.</p>
                ) : null}
              </li>
            );
          })}
        </ol>
      </CardContent>
    </Card>
  );
}
